/**
 * Central gate for which admin accounts may hand out privileges.
 *
 * Three classes of action are gated for the accounts listed below:
 *   - Comps: anything an admin can grant without a real purchase — comped
 *     memberships (Mark as Paid), VIP, parking, children tickets, lifetime
 *     status, Golden Wookiee, and minted pool codes.
 *   - Discounts: giving, changing or removing a member's membership discount.
 *   - Admin: granting the admin role to another member.
 *
 * Gated accounts keep full admin access everywhere else — they can still view
 * members, edit details, manage other roles, etc. Only the granting actions
 * above are treated as a no-op. This is the single source of truth so the
 * client stores and the server endpoints stay in sync; import from here rather
 * than hard-coding identities elsewhere.
 *
 * Matching is primarily by Firebase UID (stable across email/profile changes),
 * with email kept as a secondary safety net.
 */

/** Firebase Auth UIDs of gated accounts. */
const GATED_UIDS: ReadonlySet<string> = new Set([
  'yo0aHRXUIxMmC4qhWXktJ9Xzdhs1', // Richard Riggs
])

/** Lower-cased emails of gated accounts (secondary match). */
const GATED_EMAILS: ReadonlySet<string> = new Set([
  'riggsrichard13@gmail.com',
])

/** Minimal shape of an authenticated identity from the client or a session. */
export interface GranterIdentity {
  uid?: string | null
  email?: string | null
}

function isGatedAccount(identity?: GranterIdentity | null): boolean {
  if (!identity) return false
  const uid = identity.uid?.trim()
  if (uid && GATED_UIDS.has(uid)) return true
  const email = identity.email?.trim().toLowerCase()
  if (email && GATED_EMAILS.has(email)) return true
  return false
}

/**
 * True if the account may issue complimentary products. Unknown/empty callers
 * default to allowed so ordinary flows are never affected.
 */
export function canAwardComps(identity?: GranterIdentity | null): boolean {
  return !isGatedAccount(identity)
}

/**
 * True if the account may give, change or remove a member's membership
 * discount. Unknown/empty callers default to allowed.
 */
export function canGrantDiscounts(identity?: GranterIdentity | null): boolean {
  return !isGatedAccount(identity)
}

/**
 * True if the account may grant the admin role to another member. Unknown/empty
 * callers default to allowed.
 */
export function canGrantAdmin(identity?: GranterIdentity | null): boolean {
  return !isGatedAccount(identity)
}

/**
 * True if the viewer may see the audit history (`audit_log`, the History
 * panels). Full admins only — band and redshirt admins don't get it — and not
 * gated accounts: their comp actions are silent no-ops, and a history that is
 * missing them would give that away.
 */
export function canViewAuditHistory(
  member?: { roles?: { admin?: boolean } | null } | null,
  identity?: GranterIdentity | null,
): boolean {
  return member?.roles?.admin === true && !isGatedAccount(identity)
}
